/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable react/jsx-no-useless-fragment */
import { NotificationsContext } from 'shared-components/contexts/NotificationsContext'
import { BasicStyledComponent, DSFormFieldDescription } from 'shared-definitions/types'
import clsx from 'clsx'
import styles from './FormBuilder.module.css'
import stylesForm from 'shared-components/styles/form.module.css'
import { emailRegexp } from 'shared-code/regexp'
import { useForm } from 'react-hook-form'
import { CommonDataContext } from 'shared-components/contexts/CommonDataContext'

interface ErrorProps {
  messages: Record<string, string>
  errorType?: string
}
const Error: React.VFC<ErrorProps> = ({ messages, errorType }) => {
  if (errorType && messages[errorType]) {
    return (
      <div data-error className={styles.errorMsg}>
        {messages[errorType]}
      </div>
    )
  }
  return null
}

interface RenderProps {
  components: React.ReactElement
}

type RenderFunc = (props: RenderProps) => React.ReactElement
type FieldProps = DSFormFieldDescription & BasicStyledComponent

interface FormBuilderProps extends BasicStyledComponent {
  fields: readonly FieldProps[]
  children: RenderFunc
  onSubmit: (data: any) => Promise<boolean>
}

const FormBuilder: React.VFC<FormBuilderProps> = ({ className, fields, children, onSubmit }) => {
  const {
    reset,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ mode: 'onTouched' })
  const { enqueue } = NotificationsContext.useContainer()
  const {
    common: { strings },
  } = CommonDataContext.useContainer()

  function registerFormProps(
    item: FieldProps,
    opts: Parameters<typeof register>[1] = {}
  ): { defaultValue: string; className: string; placeholder: string } & ReturnType<
    typeof register
  > {
    return {
      ...register(item.name, {
        required: item.required,
        // value: item.value ?? '',
        ...opts,
      }),
      className: clsx(stylesForm.input, styles.inputBuilder, {
        [stylesForm.error]: errors[item.name],
      }),
      placeholder: item.placeholder + (item.required ? ' *' : ''),
      defaultValue: item.value ?? '',
    }
  }

  const components = fields.map((item, index) => {
    const containerClassName = clsx(item.className, styles.container)
    switch (item.type) {
      case 'hidden':
        if (!item.value) {
          return null
        }
        return <input {...registerFormProps(item)} type="hidden" key={index} />
      case 'email':
        return (
          <div key={index} className={containerClassName}>
            <input {...registerFormProps(item, { pattern: emailRegexp })} type={item.type} />
            <Error
              messages={{ required: strings.errRequired, pattern: strings.errEmail }}
              errorType={errors[item.name]?.type?.toString()}
            />
          </div>
        )
      case 'text':
        return (
          <div key={index} className={containerClassName}>
            <input {...registerFormProps(item)} type={item.type} />
            <Error
              messages={{ required: strings.errRequired }}
              errorType={errors[item.name]?.type?.toString()}
            />
          </div>
        )
      case 'textarea':
        return (
          <div key={index} className={containerClassName}>
            <textarea {...registerFormProps(item)} />
            <Error
              messages={{ required: strings.errRequired }}
              errorType={errors[item.name]?.type?.toString()}
            />
          </div>
        )
      default:
        return null
    }
  })

  return (
    <form
      className={className}
      onSubmit={handleSubmit(async data => {
        const result = await onSubmit(data)

        if (result) {
          enqueue({ msg: strings.msgSuccess, duration: 3000, variant: 'success' })
          reset()
        } else {
          enqueue({ msg: strings.errGeneral, duration: 3000, variant: 'error' })
        }
      })}
    >
      {children({ components: <>{components}</> })}
    </form>
  )
}

export default FormBuilder
